import React, { useEffect, useState } from 'react';
import { Col, Row, OverlayTrigger } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { selectConsumerUnit } from 'store/consumer_unit/consumer_unit.selector';
import {
  selectCreateInvoiceLoading,
  selectInvoice,
  selectInvoiceData,
  selectInvoiceError,
} from 'store/invoice/invoice.selector';
import { selectModalInvoiceState } from 'store/modal_invoice/modal_invoice.selector';
import { currentUser } from 'store/user/user.selector';
import { canIntegrateMonitoring } from 'utils/planRules';
import { InvoiceHistoryNotice } from 'components/setup-wizard/invoice-history-notice.component';
import { ManualGenerationHelp } from 'components/setup-wizard/manual-generation-help.component';

import { FormError } from 'entities/Errors';

import { setModalInvoiceState } from 'store/modal_invoice/modal_invoice.slice';
import { setConsumerUnitRefresh } from 'store/consumer_unit/consumer_unit.slice';
import { setInvoiceRefresh } from 'store/invoice/invoice.slice';

import { createInvoice } from 'store/invoice/invoice.action';
import { buildCreateInvoicePayload } from 'utils/invoicePayload';

import { alert } from 'lib/alert';

import { format } from 'utils/format';
import { numbersMask } from 'utils/masks';
import { getUtilityCompanyLogo } from 'utils/logo';

import consumerUnitImg from 'assets/images/consumer_unit.jpeg';

import { LoadingInvoice } from '../loading-invoice/loading-invoice.component';
import { InvoiceWarnings } from 'components/invoice-warnings/invoice-warnings.component';

import { BsExclamationCircleFill } from 'react-icons/bs';

import {
  Button,
  Cancel,
  CardDataContainer,
  CardImage,
  Company,
  CompanyImg,
  ConsumerUnitCardContainer,
  ConsumerUnitInfo,
  Footer,
  FormInput,
  FormLabel,
  Icon,
  InfoAddress,
  InfoData,
  InfoIcon,
  InfoTitle,
  InvoiceInfo,
  MonthGenerationContainer,
  SaveInvoiceContainer,
  FormGroup,
  InfoReplaceContainer,
  InfoReplaceContent,
  InvoiceInfoTitle,
  InvoiceInfoData,
  InvoiceTooltip,
  NoExpectedGenerationInline,
  PromotionNotice,
} from './save-invoice.styles';
import { periodConsumptionKwh } from 'utils/invoiceConsumption';
import { parseDecimalInput } from 'utils/decimalInput';
import { KwhInput } from 'components/kwh-input/kwh-input.component';

interface FormProps {
  monthGeneration: string;
}

export const SaveInvoice = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [expectedGeneration, setExpectedGeneration] = useState<string>('');

  const [formFields, setFormFields] = useState<FormProps>({} as FormProps);
  const [formError, setFormError] = useState<FormError>({} as FormError);

  const consumerUnit = useAppSelector(selectConsumerUnit);
  const modalState = useAppSelector(selectModalInvoiceState);
  const invoiceData = useAppSelector(selectInvoiceData);
  const createInvoiceLoading = useAppSelector(selectCreateInvoiceLoading);
  const invoiceError = useAppSelector(selectInvoiceError);
  const invoice = useAppSelector(selectInvoice);
  const user = useAppSelector(currentUser);
  // Free (out/2026): sem conexão com o inversor — a geração é digitada aqui
  const manualGeneration = !canIntegrateMonitoring(user?.plan?.name ?? 'Free');

  // Opção B: a fatura indica geração própria (hasFv extraído) mas a UC ainda está
  // marcada como não-GD. Ao salvar, o backend promove a UC a GD; aqui avisamos e,
  // após salvar, encaminhamos para a aba de usina para cadastrar o monitoramento.
  // Receptoras (só recebem crédito) vêm com hasFv=false e não entram aqui.
  const isPromotingToGd = Boolean(invoiceData?.hasFv) && !consumerUnit?.hasFv;

  useEffect(() => {
    if (modalState === 'save_invoice') {
      if (
        consumerUnit?.hasFv &&
        invoiceData &&
        !invoiceData.consumerUnitExists
      ) {
        const month = invoiceData.refMonth.slice(-2);

        const expectedGeneration = consumerUnit.plantList
          .reduce((acc, item) => {
            const value =
              item.plantExpectedGenerationList.find((x) => x.month === month)
                ?.value || 0;

            return acc + value;
          }, 0)
          .toString();

        setExpectedGeneration(expectedGeneration);
        const generatedEnergy =
          consumerUnit.monthGenerationList.find(
            (x) => x.refMonth === invoiceData.refMonth
          )?.value || 0;

        setFormFields({
          monthGeneration: String(generatedEnergy),
        });
      } else if (consumerUnit?.hasFv && invoiceData?.consumerUnitExists) {
        const month = invoiceData.refMonth.slice(-2);

        const expectedGeneration = consumerUnit.plantList.reduce(
          (acc, item) => {
            const value =
              item.plantExpectedGenerationList.find((x) => x.month === month)
                ?.value || 0;
            return acc + value;
          },
          0
        );

        setExpectedGeneration(expectedGeneration.toString());

        setFormFields({
          monthGeneration:
            invoiceData?.monthGeneration && invoiceData.monthGeneration !== -1
              ? String(invoiceData.monthGeneration)
              : '',
        });
      }
    }
  }, [consumerUnit]);

  useEffect(() => {
    if (!createInvoiceLoading && modalState === 'save_invoice') {
      if (invoice && !invoiceError) {
        dispatch(setModalInvoiceState({ modalState: 'hidden' }));
        if (invoiceData?.consumerUnitExists) {
          dispatch(setConsumerUnitRefresh({ refresh: true }));
          dispatch(setInvoiceRefresh({ refresh: true }));
        }
        if (isPromotingToGd && consumerUnit && !manualGeneration) {
          alert.success(
            'Fatura salva! Detectamos geração própria nesta unidade — conecte o inversor para a geração entrar sozinha.'
          );
          navigate(`/unidades-consumidoras/${consumerUnit.id}?aba=usina`);
        } else {
          alert.success('Fatura salva com sucesso!');
          navigate('/dashboard');
        }
      } else if (!invoice && invoiceError) {
        if (invoiceError.type === 'message') {
          alert.error(invoiceError.message);
        } else {
          const validationErrors = invoiceError.details?.reduce((acc, item) => {
            return { ...acc, [item.key]: item.message };
          }, {} as FormError);

          setFormError(validationErrors);
        }
      }
    }
  }, [createInvoiceLoading]);

  const { monthGeneration } = formFields;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormFields({ ...formFields, [name]: numbersMask(value) });
  };

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (invoiceData && consumerUnit) {
      dispatch(
        createInvoice(
          buildCreateInvoicePayload(invoiceData, {
            consumerUnitId: consumerUnit.id,
            // Opção B: ao promover, envia o hasFv da FATURA (true); o backend liga
            // o flag na UC. Caso normal segue o hasFv já salvo na UC.
            hasFv: consumerUnit.hasFv || isPromotingToGd,
            monthGeneration: monthGeneration
              ? parseDecimalInput(monthGeneration)
              : -1,
          })
        )
      );
    }
  };

  // Como o X do modal: cancelar não salva nada, então o usuário fica onde estava.
  const handleCancel = () => {
    dispatch(setInvoiceRefresh({ refresh: true }));
    dispatch(setModalInvoiceState({ modalState: 'hidden' }));
  };

  const handleConfigureExpectedGeneration = () => {
    dispatch(setModalInvoiceState({ modalState: 'hidden' }));
    navigate(`/unidades-consumidoras/${consumerUnit?.id}`);
  };

  if (createInvoiceLoading) {
    return <LoadingInvoice />;
  }

  return (
    <>
      {invoiceData && consumerUnit && (
        <SaveInvoiceContainer>
          <InvoiceWarnings
            warnings={invoiceData.readingWarnings}
            refMonth={invoiceData.refMonth}
          />
          {invoiceData.invoiceExists && (
            // Reenvio do mesmo mês SUBSTITUI a fatura gravada (é assim que
            // entra uma fatura revista pela distribuidora) — avisa antes.
            <PromotionNotice>
              <BsExclamationCircleFill />
              <p>
                Já existe uma fatura de{' '}
                <b>{invoiceData.refMonth.split('_').reverse().join('/')}</b>{' '}
                nesta unidade. Ao salvar, ela será <b>substituída</b> por esta.
              </p>
            </PromotionNotice>
          )}
          <InvoiceInfo>
            <InvoiceInfoTitle>
              <h2>Informações da conta</h2>
            </InvoiceInfoTitle>
            <InvoiceInfoData>
              <InfoData>{format.refMonth(invoiceData.refMonth)}</InfoData>
              <Icon src="/dot.svg" className="ms-2 me-2" alt="" />
              <InfoData>{`R$ ${format.money(
                invoiceData.invoiceValue
              )}`}</InfoData>
              <Icon src="/dot.svg" className="ms-2 me-2" alt="" />
              <InfoData>
                <svg
                  width="13"
                  height="14"
                  viewBox="0 0 13 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.48526 0.79162C4.53611 0.618727 4.69479 0.5 4.875 0.5H8.125C8.25559 0.5 8.37822 0.562774 8.45457 0.668711C8.53093 0.774649 8.5517 0.910832 8.51041 1.03472L7.06365 5.375H10.1563C10.3114 5.375 10.453 5.46338 10.5212 5.60276C10.5894 5.74215 10.5722 5.90819 10.4769 6.03066L4.78943 13.3432C4.66902 13.498 4.45521 13.5447 4.28124 13.4541C4.10726 13.3636 4.02279 13.1617 4.08047 12.9743L5.54371 8.21875H2.84375C2.71572 8.21875 2.59518 8.15839 2.51848 8.05588C2.44178 7.95337 2.41789 7.8207 2.45401 7.69787L4.48526 0.79162Z"
                    fill="#F79422"
                  />
                </svg>
                {`${format.energy(periodConsumptionKwh(invoiceData))} kWh`}
              </InfoData>
            </InvoiceInfoData>
          </InvoiceInfo>
          <ConsumerUnitInfo>
            <div>
              <p className="consumer-unit-info-label">
                Esta conta de luz está vinculada a uma{' '}
                <b>unidade consumidora</b> já cadastrada:
              </p>
            </div>
            <ConsumerUnitCardContainer>
              <CardImage
                style={{
                  backgroundImage: `url(${consumerUnitImg})`,
                }}
              ></CardImage>
              <CardDataContainer>
                <InfoTitle className="info-title">
                  {consumerUnit?.name}
                </InfoTitle>
                <div>
                  <div>
                    <InfoAddress>
                      <Icon
                        src="/geo-alt-fill.svg"
                        className="me-2 icon-grade"
                        alt="Localização"
                      ></Icon>
                      {`${consumerUnit.city?.name}/${consumerUnit.city?.state.code}`}
                    </InfoAddress>
                  </div>
                </div>
              </CardDataContainer>
              <Company className="company">
                <CompanyImg
                  src={getUtilityCompanyLogo(consumerUnit.utilityCompany.slug)}
                />
              </Company>
            </ConsumerUnitCardContainer>
          </ConsumerUnitInfo>
          <InvoiceHistoryNotice
            refMonth={invoiceData.refMonth}
            history={invoiceData.monthConsumptionList}
          />
          {isPromotingToGd && (
            <PromotionNotice>
              <BsExclamationCircleFill />
              <p>
                Esta fatura indica <b>geração própria</b>.{' '}
                {manualGeneration
                  ? 'Informe abaixo quanto o sistema gerou no período da fatura.'
                  : 'Ao salvar, vamos para a conexão do inversor, e a geração passa a entrar sozinha em cada fatura.'}
              </p>
            </PromotionNotice>
          )}
          <MonthGenerationContainer>
            <form onSubmit={handleSubmit} noValidate>
              {(consumerUnit.hasFv ||
                (manualGeneration && isPromotingToGd)) && (
                <>
                  <div>
                    {manualGeneration ? (
                      <p>
                        Quanto o seu sistema gerou de{' '}
                        <b>
                          {new Date(
                            invoiceData.read.previousDate
                          ).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                          })}{' '}
                          a{' '}
                          {new Date(
                            invoiceData.read.currentDate
                          ).toLocaleDateString('pt-BR')}
                        </b>
                        ? Veja no app do seu inversor e digite em kWh. O app só
                        mostra o mês inteiro? Use o total do mês — a diferença é
                        pequena.
                      </p>
                    ) : (
                      <p>
                        Informe os valores de geração para que os dados de
                        consumo deste mês sejam atualizados
                      </p>
                    )}
                  </div>
                  <Row>
                    <Col md={5}>
                      <FormGroup hasError={!!formError.monthGeneration}>
                        <FormLabel>
                          {manualGeneration
                            ? 'Geração do período (kWh)'
                            : 'Geração real'}
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            overlay={(props) => (
                              <InvoiceTooltip {...props}>
                                A geração real de um sistema fotovoltaico é a
                                quantidade efetiva de energia que os painéis
                                solares produziram no mês.
                              </InvoiceTooltip>
                            )}
                          >
                            <div>
                              <InfoIcon />
                            </div>
                          </OverlayTrigger>
                        </FormLabel>
                        <KwhInput
                          component={FormInput}
                          name="monthGeneration"
                          type="text"
                          value={monthGeneration}
                          onChange={handleChange}
                        />
                        {formError.monthGeneration && (
                          <span>{formError.monthGeneration}</span>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={{ span: 5, offset: 2 }}>
                      {Number(expectedGeneration) === 0 &&
                      expectedGeneration !== '' ? (
                        <NoExpectedGenerationInline>
                          <BsExclamationCircleFill />
                          <p>
                            Não pudemos comparar a geração medida com a prevista
                          </p>
                          <button
                            type="button"
                            onClick={handleConfigureExpectedGeneration}
                          >
                            Configurar geração prevista
                          </button>
                        </NoExpectedGenerationInline>
                      ) : (
                        <FormGroup hasError={!!formError.expectedGeneration}>
                          <FormLabel>
                            Geração esperada
                            <OverlayTrigger
                              delay={{ hide: 450, show: 300 }}
                              overlay={(props) => (
                                <InvoiceTooltip {...props}>
                                  A geração esperada de um sistema fotovoltaico
                                  é a geração prevista de energia de acordo com
                                  o projeto inicial.
                                </InvoiceTooltip>
                              )}
                            >
                              <div>
                                <InfoIcon />
                              </div>
                            </OverlayTrigger>
                          </FormLabel>
                          <FormInput
                            name="expectedGeneration"
                            type="text"
                            value={format.energy(Number(expectedGeneration))}
                            disabled
                          />
                        </FormGroup>
                      )}
                    </Col>
                  </Row>
                </>
              )}
              {manualGeneration && (consumerUnit.hasFv || isPromotingToGd) && (
                <ManualGenerationHelp
                  onNavigate={() =>
                    dispatch(setModalInvoiceState({ modalState: 'hidden' }))
                  }
                />
              )}
              <Footer>
                <Cancel onClick={handleCancel}>Cancelar</Cancel>
                <Button type="submit">
                  {manualGeneration &&
                  (consumerUnit.hasFv || isPromotingToGd) &&
                  !monthGeneration
                    ? 'Salvar e informar depois'
                    : 'Salvar'}
                </Button>
              </Footer>
            </form>
          </MonthGenerationContainer>
        </SaveInvoiceContainer>
      )}
    </>
  );
};
